<template>
  <v-form v-model="valid" :disabled="edit_mode && !day_report.can_edit">
    <v-menu
      ref="menu"
      v-model="datepicker"
      v-model:return-value="day_report_edit.date"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="290px"
    >
      <template #activator="{ props }">
        <v-text-field
          v-model="computedDateFormatted"
          label="Datum veldwerkverslag"
          prepend-icon="event"
          outlined
          required
          readonly
          v-bind="props"
          :error-messages="dateErrors"
          dense
          @input="v$.day_report_edit.date.$touch()"
          @blur="v$.day_report_edit.date.$touch()"
        ></v-text-field>
      </template>

      <v-date-picker v-model="datePickerValue" no-title scrollable>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="datepicker = false">Cancel</v-btn>
        <v-btn text color="primary" @click="$refs.menu.save(datePickerValue)"
          >OK</v-btn
        >
      </v-date-picker>
    </v-menu>

    <v-select
      v-model="day_report_edit.user_id"
      :items="users"
      label="Veldwerkverslag eigenaar"
      dense
      prepend-icon="mdi-account"
      outlined
      item-title="name"
      item-value="id"
      :disabled="hide_user"
      :error-messages="dayReportUserErrors"
      return-object
      @input="v$.day_report_edit.user_id.$touch()"
      @blur="v$.day_report_edit.user_id.$touch()"
    >
      <template #selection="{ item }">
        {{ item.raw.name }}
      </template>
    </v-select>

    <!-- <v-select
          v-model="day_report_edit.day_report_type"
          :items="items"
          item-text="name"
          item-value="value"
          label="Type veldwerkverslag"
          prepend-icon="event"
          outlined
          dense
          :error-messages="dayReportTypeErrors"
          @input="$v.day_report_edit.day_report_type.$touch()"
          @blur="$v.day_report_edit.day_report_type.$touch()"
          @change="setDefaultDuration"
        ></v-select> -->

    <v-select
      v-model="day_report_edit.day_report_type_id"
      :items="day_report_types"
      item-title="name"
      item-value="id"
      label="Type veldwerkverslag"
      prepend-icon="event"
      outlined
      dense
      :error-messages="dayReportTypeErrors"
      return-object
      @input="v$.day_report_edit.day_report_type_id.$touch()"
      @blur="v$.day_report_edit.day_report_type_id.$touch()"
    >
      <template #selection="{ item }">
        {{ item.raw.name }}
      </template>
    </v-select>

    <div v-if="show_timepicker">
      <!-- Flexible duration for specific types (e.g. Balance) -->
      <div v-if="isFlexibleDurationType">
        <v-text-field
          :model-value="flexibleDurationInput"
          label="Tijdsbesteding (uren)"
          prepend-icon="mdi-clock-edit-outline"
          outlined
          dense
          hint="Voer uren in (bijv. -1,5 of 2.25). Bereik: -999,99 tot 999,99"
          persistent-hint
          :error-messages="durationErrors"
          @update:model-value="handleFlexibleDurationInput"
          @blur="v$.day_report_edit.duration.$touch()"
        ></v-text-field>
      </div>

      <!-- Plan mode: Dual slider approach for easy hour/minute selection -->
      <div v-else-if="plan_mode">
        <v-text-field
          :model-value="formattedDuration"
          label="Tijdsbesteding"
          prepend-icon="mdi-clock"
          outlined
          dense
          readonly
          :error-messages="durationErrors"
          @blur="v$.day_report_edit.duration.$touch()"
        ></v-text-field>

        <!-- Hours slider for plan mode (0-40 hours) -->
        <v-slider
          v-model="planModeHours"
          :min="0"
          :max="40"
          :step="1"
          label="Uren"
          thumb-label="always"
          :thumb-size="24"
          class="mt-2"
          @update:model-value="updatePlanModeDuration"
        >
          <template #thumb-label="{ modelValue }"> {{ modelValue }}u </template>
        </v-slider>

        <!-- Minutes slider for plan mode (0, 15, 30, 45 minutes) -->
        <v-slider
          v-model="planModeMinutes"
          :min="0"
          :max="45"
          :step="15"
          label="Minuten"
          thumb-label="always"
          :thumb-size="24"
          class="mt-2"
          @update:model-value="updatePlanModeDuration"
        >
          <template #thumb-label="{ modelValue }">
            {{ modelValue }}min
          </template>
        </v-slider>
      </div>

      <!-- Regular mode: Time picker and slider -->
      <div v-else>
        <v-menu
          ref="timeMenu"
          v-model="timepicker"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="290px"
        >
          <template #activator="{ props }">
            <v-text-field
              v-model="formattedDuration"
              label="Tijdsbesteding"
              prepend-icon="mdi-clock"
              outlined
              dense
              readonly
              v-bind="props"
              :error-messages="durationErrors"
            ></v-text-field>
          </template>

          <v-time-picker
            v-model="timePickerValue"
            format="24hr"
            :allowed-minutes="allowedMinutes"
            scrollable
            @update:model-value="onTimeChange"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="timepicker = false"
              >Annuleren</v-btn
            >
            <v-btn text color="primary" @click="confirmTime">OK</v-btn>
          </v-time-picker>
        </v-menu>

        <v-slider
          v-model="hourSlider"
          :min="0"
          :max="24"
          :step="0.25"
          label="Uren"
          thumb-label="always"
          :thumb-size="24"
          class="mt-4"
          @update:model-value="updateTimeFromSliders"
        >
          <template #thumb-label="{ modelValue }">
            {{ formatHourLabel(modelValue) }}
          </template>
        </v-slider>
      </div>
    </div>

    <!-- Single save button -->
    <v-btn class="mt-2" block color="primary" @click="submit">{{
      submitButtonText
    }}</v-btn>
  </v-form>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { useDayReportsStoreRefactor } from "@/stores/dayReportsStoreRefactor";
import { useDayReportTypesStoreRefactor } from "@/stores/dayReportTypesStoreRefactor";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import moment from "moment";
import { useVuelidate } from "@vuelidate/core";
import { useAlertStore } from "@/stores/alertStore";
import { required } from "@vuelidate/validators";

export default {
  name: "DayReportForm",
  props: {
    day_report: {
      type: Object,
      required: true,
    },
    submitButtonText: {
      type: String,
      default: "Opslaan",
    },
    popup: {
      type: Boolean,
      default: false,
    },
    hide_user: {
      type: Boolean,
      default: false,
    },
    plan_mode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:day_report", "saved"],
  setup(props, { emit }) {
    const authStore = useAuthStoreRefactor();
    const dayReportsStore = useDayReportsStoreRefactor();
    const dayReportTypesStore = useDayReportTypesStoreRefactor();
    const usersStore = useUsersStoreRefactor();
    const alertStore = useAlertStore();

    const loading = ref(false);
    const valid = ref(true);

    const rules = ref({
      day_report_edit: {
        date: { required },
        day_report_type_id: { required },
        user_id: { required },
        duration: {},
        // No duration validation - backend handles this and returns error if needed
      },
    });

    const users = computed(() => {
      return usersStore.activeUsersInCurrentTenant || [];
    });

    const day_report_types = computed(() => {
      return dayReportTypesStore.dayReportTypes
        .filter((type) => type.active)
        .sort((a, b) => a.name.localeCompare(b.name));
    });

    const day_report_edit = ref({});
    const datepicker = ref(false);
    const timepicker = ref(false);
    const timePickerValue = ref("08:00");
    const hourSlider = ref(8);

    // Plan mode sliders
    const planModeHours = ref(8);
    const planModeMinutes = ref(0);

    const v$ = useVuelidate(rules, { day_report_edit });

    const isFlexibleDurationType = computed(() => {
      // Check if we have a day report type selected
      if (day_report_edit.value.day_report_type_id) {
        let typeId;

        // Handle both object and direct ID cases
        if (typeof day_report_edit.value.day_report_type_id === "object") {
          typeId = day_report_edit.value.day_report_type_id.id;
        } else {
          typeId = day_report_edit.value.day_report_type_id;
        }

        // Find the type in the day report types store
        const selectedType = dayReportTypesStore.dayReportTypes.find(
          (type) => type.id === typeId,
        );

        // Flexible duration for "vrij_balans" or any Balance type
        return (
          selectedType?.code === "vrij_balans" ||
          selectedType?.type === "DayReportType::Balance"
        );
      }
      return false;
    });

    const flexibleDurationInput = ref("");

    // Watch for duration changes and update the raw input field
    // but only if the value has actually changed numerically
    watch(
      () => day_report_edit.value.duration,
      (newVal) => {
        if (newVal === undefined || newVal === null) {
          flexibleDurationInput.value = "";
          return;
        }

        const hours = newVal / 3600;
        const currentInputHours = parseFloat(
          String(flexibleDurationInput.value).replace(",", "."),
        );

        if (isNaN(currentInputHours) || Math.abs(currentInputHours - hours) > 0.0001) {
          // If it's a whole number, don't show decimals. Otherwise show up to 2.
          flexibleDurationInput.value = Number.isInteger(hours)
            ? hours.toString()
            : hours.toFixed(2).replace(".", ",");
        }
      },
      { immediate: true },
    );

    const handleFlexibleDurationInput = (val) => {
      flexibleDurationInput.value = val;
      if (val === "" || val === null) {
        day_report_edit.value.duration = 0;
        return;
      }

      // Handle both dot and comma as decimal separator
      const normalizedValue = String(val).replace(",", ".");
      const parsed = parseFloat(normalizedValue);

      if (!isNaN(parsed)) {
        // Convert hours back to seconds
        day_report_edit.value.duration = Math.round(parsed * 3600);
      }
    };

    const show_timepicker = computed(() => {
      // Check if we have a day report type selected
      if (day_report_edit.value.day_report_type_id) {
        let typeId;

        // Handle both object and direct ID cases
        if (typeof day_report_edit.value.day_report_type_id === "object") {
          typeId = day_report_edit.value.day_report_type_id.id;
        } else {
          typeId = day_report_edit.value.day_report_type_id;
        }

        // Find the type in the day report types store
        const selectedType = dayReportTypesStore.dayReportTypes.find(
          (type) => type.id === typeId,
        );

        // Show time picker if the selected type has direct_time
        if (selectedType?.direct_time === true) {
          console.log(
            "Showing time picker for type with direct_time:",
            selectedType.name,
          );
          return true;
        }
      }

      // Also show time picker for existing reports that aren't planned
      return (
        day_report_edit.value.id &&
        day_report_edit.value.day_report_status != "planned"
      );
    });

    const edit_mode = computed(() => {
      return !!day_report_edit.value.id;
    });

    const computedDateFormatted = computed(() => {
      return day_report_edit.value.date
        ? moment(day_report_edit.value.date).format("DD-MM-YYYY")
        : "";
    });

    // Create a computed property for the date picker to handle date values properly
    const datePickerValue = computed({
      get() {
        // Always return the date as a string in YYYY-MM-DD format
        return day_report_edit.value.date || moment().format("YYYY-MM-DD");
      },
      set(value) {
        // Ensure the value is always stored as a YYYY-MM-DD string without timezone conversion
        if (value) {
          // If value is a JavaScript Date object, format it properly
          if (value instanceof Date) {
            // Use the date components directly to avoid timezone issues
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, "0");
            const day = String(value.getDate()).padStart(2, "0");
            day_report_edit.value.date = `${year}-${month}-${day}`;
          } else if (typeof value === "string") {
            // If it's already a string, ensure it's in the right format
            if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
              day_report_edit.value.date = value;
            } else {
              // Try to parse with moment and reformat
              const parsed = moment(value);
              if (parsed.isValid()) {
                day_report_edit.value.date = parsed.format("YYYY-MM-DD");
              }
            }
          }
        } else {
          day_report_edit.value.date = moment().format("YYYY-MM-DD");
        }
        console.log(
          "DayReport date picker set to:",
          day_report_edit.value.date,
        );
      },
    });

    const dateErrors = computed(() => {
      return v$.value.day_report_edit.date.$error
        ? ["Datum is verplicht."]
        : [];
    });

    const durationErrors = computed(() => {
      // No frontend validation for duration - backend handles this
      return [];
    });

    const dayReportTypeErrors = computed(() => {
      return v$.value.day_report_edit.day_report_type_id.$error
        ? ["Type is verplicht."]
        : [];
    });

    const dayReportUserErrors = computed(() => {
      return v$.value.day_report_edit.user_id.$error
        ? ["Eigenaar is verplicht."]
        : [];
    });

    // Utility functions for duration conversion
    function secondsToHHMM(seconds) {
      if (!seconds && seconds !== 0) return "8:00";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}:${String(minutes).padStart(2, "0")}`;
    }

    function hhmmToSeconds(hhmmString) {
      if (!hhmmString) return 8 * 3600; // Default 8 hours
      const [hours, minutes] = hhmmString.split(":").map(Number);
      return (hours || 0) * 3600 + (minutes || 0) * 60;
    }

    const formattedDuration = computed(() => {
      return secondsToHHMM(day_report_edit.value.duration);
    });

    function allowedMinutes(minute) {
      return minute % 15 === 0;
    }

    function onTimeChange(time) {
      if (!time) return;

      const [hoursStr, minutesStr] = time.split(":");
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      if (isNaN(hours) || isNaN(minutes)) return;

      // Set slider value with 15-minute precision
      if (minutes >= 45) {
        hourSlider.value = hours + 0.75;
      } else if (minutes >= 30) {
        hourSlider.value = hours + 0.5;
      } else if (minutes >= 15) {
        hourSlider.value = hours + 0.25;
      } else {
        hourSlider.value = hours;
      }

      // Set actual minutes based on allowed values (0, 15, 30, 45)
      let roundedMinutes = 0;
      if (minutes >= 45) {
        roundedMinutes = 45;
      } else if (minutes >= 30) {
        roundedMinutes = 30;
      } else if (minutes >= 15) {
        roundedMinutes = 15;
      }

      updateDurationString(hours, roundedMinutes);
    }

    function confirmTime() {
      timepicker.value = false;
      v$.value.day_report_edit.duration.$touch();
    }

    function updateTimeFromSliders() {
      // Calculate hours and minutes from slider value (supports 15-minute increments)
      const hours = Math.floor(hourSlider.value);
      const fractionalPart = hourSlider.value - hours;

      // Convert fractional part to minutes (0.25 = 15 min, 0.5 = 30 min, 0.75 = 45 min)
      const minutes = Math.round(fractionalPart * 60);

      updateDurationString(hours, minutes);
    }

    function updateDurationString(hours, minutes) {
      // Convert to seconds and store in duration field
      day_report_edit.value.duration = hours * 3600 + minutes * 60;

      // Only update time picker if hours are within 24-hour format (time picker limitation)
      if (hours < 24) {
        timePickerValue.value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }
    }

    // Update duration from plan mode sliders
    function updatePlanModeDuration() {
      const hours = planModeHours.value;
      const minutes = planModeMinutes.value;

      // Convert to seconds
      day_report_edit.value.duration = hours * 3600 + minutes * 60;
    }

    function prepareFormData() {
      // Convert object selections back to IDs for submission
      const formData = { ...day_report_edit.value };

      // If user_id is an object, extract the ID
      if (formData.user_id && typeof formData.user_id === "object") {
        formData.user_id = formData.user_id.id;
      }

      // If day_report_type_id is an object, extract the ID
      let dayReportTypeId = formData.day_report_type_id;
      if (
        formData.day_report_type_id &&
        typeof formData.day_report_type_id === "object"
      ) {
        dayReportTypeId = formData.day_report_type_id.id;
        formData.day_report_type_id = dayReportTypeId;
      }

      // Get the selected day report type
        const selectedDayReportType = dayReportTypesStore.dayReportTypes.find(
          (type) => type.id === dayReportTypeId,
        );

      // Set status to "planned" if in plan_mode and it's a working day report type
      if (
        props.plan_mode &&
        selectedDayReportType?.working &&
        !edit_mode.value
      ) {
          formData.day_report_status = "planned";
          console.log(
            "Setting day_report_status to 'planned' for working day report type in plan mode",
          );
        }

      // For WORKING types when CREATING: Remove duration, let backend set default
      // For NON-WORKING types (direct_time): Keep duration from frontend
      // For EDITING: Always keep duration (user may have adjusted it)
      if (
        !edit_mode.value &&
        selectedDayReportType &&
        !selectedDayReportType.direct_time
      ) {
        console.log(
          `Working type - removing duration, backend will set default`,
        );
        delete formData.duration;
      } else if (formData.duration) {
        console.log(`Duration in seconds: ${formData.duration}`);
      }

      console.log("Prepared form data:", formData);

      return formData;
    }

    async function submit() {
      v$.value.$touch();

      if (v$.value.$invalid) {
        console.error("Form validation failed:", v$.value.$errors);
        alertStore.error("Controleer de gemarkeerde velden");
        return;
      }

      loading.value = true;

      try {
        const formData = prepareFormData();
        let result;

        // Log what we're about to do
        console.log(
          `${edit_mode.value ? "Updating" : "Creating"} day report:`,
          formData,
        );
        console.log(
          "DATE-DEBUG: DayReport date being submitted:",
          formData.date,
        );
        console.log("DATE-DEBUG: DayReport date type:", typeof formData.date);
        console.log("DATE-DEBUG: DayReport date value breakdown:", {
          raw: formData.date,
          moment_parsed: moment(formData.date).format("YYYY-MM-DD"),
          moment_utc: moment.utc(formData.date).format("YYYY-MM-DD"),
          js_date: new Date(formData.date).toISOString().split("T")[0],
        });

        if (edit_mode.value) {
          // Always use update - closing is handled by separate button
          result = await dayReportsStore.update(formData);
          console.log("Day report updated successfully:", result);
          alertStore.success("Dagrapport succesvol bijgewerkt");
        } else {
          // Create new day report
          result = await dayReportsStore.create(formData);
          console.log("Day report created successfully:", result);
          alertStore.success("Dagrapport succesvol aangemaakt");

          // If in popup mode (mobile), refresh current user here since parent can't receive event
          // If inline mode (desktop), parent's handleDayReportSaved will handle the refresh
          if (props.popup) {
            console.log(
              "DayReportForm: Popup mode - refreshing current user after create",
            );
            await authStore.getCurrentUser(true);
            console.log(
              "DayReportForm: User refreshed, active_day_report_id:",
              authStore.user?.active_day_report_id,
            );
          }
        }

        // Emit events for parent components
        emit("update:day_report", {
          ...props.day_report,
          date: moment().format("YYYY-MM-DD"),
          day_report_type_id: "",
        });

        // Emit saved event with the result - this will close the form dialog
        emit("saved", result);

        // If we're in popup mode, the saved event will close the form
        // Otherwise, reset the form for re-use
        if (!props.popup) {
          // Reset form for next entry if not in a popup
          day_report_edit.value = {
            date: moment().format("YYYY-MM-DD"),
            user_id: authStore.user?.id || null,
            day_report_type_id: null,
            // No duration - will be set by watcher when type is selected
          };

          // Reset validation state
          v$.value.$reset();
        }
      } catch (error) {
        console.error("Error submitting day report:", error);
        alertStore.error(
          "Er is een fout opgetreden bij het opslaan van het dagrapport",
        );
      } finally {
        loading.value = false;
      }
    }

    // Initialize the time picker values better
    function initTimePickerValues() {
      if (
        !day_report_edit.value.duration &&
        day_report_edit.value.duration !== 0
      ) {
        // No default here - let day_report_type watcher or backend set it
        // Just set UI to show 8 hours as placeholder
        timePickerValue.value = "08:00";
        hourSlider.value = 8;
        planModeHours.value = 8;
        planModeMinutes.value = 0;
        return;
      }

      try {
        // Convert seconds to hours and minutes
        const totalSeconds = day_report_edit.value.duration;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        if (isNaN(hours) || isNaN(minutes)) {
          // Don't set duration value - just set UI defaults
            timePickerValue.value = "08:00";
            hourSlider.value = 8;
            planModeHours.value = 8;
            planModeMinutes.value = 0;
          return;
        }

        // Update slider with 15-minute precision
        if (minutes >= 45) {
          hourSlider.value = hours + 0.75;
        } else if (minutes >= 30) {
          hourSlider.value = hours + 0.5;
        } else if (minutes >= 15) {
          hourSlider.value = hours + 0.25;
        } else {
          hourSlider.value = hours;
        }

        // Update plan mode sliders
        planModeHours.value = hours;
        // Round minutes to nearest 15-minute increment
        planModeMinutes.value = Math.round(minutes / 15) * 15;

        // Format for time picker
        timePickerValue.value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      } catch (e) {
        console.error("Error initializing time picker:", e);
        // Don't set duration value on error - just set UI defaults
          timePickerValue.value = "08:00";
          hourSlider.value = 8;
          planModeHours.value = 8;
          planModeMinutes.value = 0;
      }
    }

    function initFormData() {
      console.log("Initializing form data from props:", props.day_report);

      // Create a copy to avoid modifying props directly
      let formData = { ...props.day_report };

      // Set default user if not provided and not in hide_user mode
      if (!formData.user_id && !props.hide_user) {
        formData.user_id = authStore.user?.id || null;
      }

      // Log the available users
      console.log(`Available users for selection: ${users.value.length}`);

      // Preserve existing duration if we're editing and it has been modified
      const preservedDuration =
        edit_mode.value && day_report_edit.value.duration
          ? day_report_edit.value.duration
          : formData.duration;

      // Ensure we have a copy to avoid reactivity issues
      day_report_edit.value = { ...formData };

      // Restore preserved duration if it exists
      if (preservedDuration) {
        day_report_edit.value.duration = preservedDuration;
      }

      // For edit mode, we need to handle day_report_type_id and user_id as objects
      if (formData.id) {
        console.log("Edit mode detected, preparing object references");

        // Try to find the day report type object
        if (
          formData.day_report_type_id &&
          typeof formData.day_report_type_id !== "object"
        ) {
          const typeId = formData.day_report_type_id;
          const typeObj = dayReportTypesStore.dayReportTypes.find(
            (type) => type.id === typeId,
          );

          if (typeObj) {
            day_report_edit.value.day_report_type_id = typeObj;
            console.log(`Found day report type: ${typeObj.name}`);
          } else {
            console.warn(`Day report type with ID ${typeId} not found`);
          }
        }

        // Try to find the user object
        if (formData.user_id && typeof formData.user_id !== "object") {
          const userId = formData.user_id;
          const userObj = users.value.find((user) => user.id === userId);

          if (userObj) {
            day_report_edit.value.user_id = userObj;
            console.log(`Found user: ${userObj.name}`);
          } else {
            console.warn(
              `User with ID ${userId} not found in ${users.value.length} users`,
            );
            // If user not found, but we have users loaded, use the current user as fallback
            if (users.value.length > 0 && authStore.user) {
              const currentUserObj = users.value.find(
                (user) => user.id === authStore.user.id,
              );
              if (currentUserObj) {
                day_report_edit.value.user_id = currentUserObj;
                console.log(
                  `Using current user as fallback: ${currentUserObj.name}`,
                );
              }
            }
          }
        }
      }

      // Initialize time picker values only if we don't have a preserved duration
      if (!preservedDuration && preservedDuration !== 0) {
        initTimePickerValues();
      } else {
        // Update the UI controls to match the preserved duration (seconds)
        try {
          const hours = Math.floor(preservedDuration / 3600);
          const minutes = Math.floor((preservedDuration % 3600) / 60);

          if (!isNaN(hours) && !isNaN(minutes)) {
            // Update slider with 15-minute precision
            if (minutes >= 45) {
              hourSlider.value = hours + 0.75;
            } else if (minutes >= 30) {
              hourSlider.value = hours + 0.5;
            } else if (minutes >= 15) {
              hourSlider.value = hours + 0.25;
            } else {
              hourSlider.value = hours;
            }

            // Update plan mode sliders
            planModeHours.value = hours;
            // Round minutes to nearest 15-minute increment
            planModeMinutes.value = Math.round(minutes / 15) * 15;

            // Format for time picker
            timePickerValue.value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
          }
        } catch (e) {
          console.error(
            "Error updating time picker for preserved duration:",
            e,
          );
        }
      }

      console.log("Form data initialized:", day_report_edit.value);
    }

    // Watch for changes in props.day_report
    watch(
      () => props.day_report,
      () => {
        initFormData();
      },
      { immediate: true },
    );

    // Watch for changes in day report type selection to set planned status in plan mode
    watch(
      () => day_report_edit.value.day_report_type_id,
      (newTypeId) => {
        if (props.plan_mode && newTypeId && !edit_mode.value) {
          // Handle both object and ID cases
          const typeId =
            typeof newTypeId === "object" ? newTypeId.id : newTypeId;
          const selectedType = dayReportTypesStore.dayReportTypes.find(
            (type) => type.id === typeId,
          );

          if (selectedType?.working) {
            day_report_edit.value.day_report_status = "planned";
            console.log(
              "Auto-setting status to 'planned' for working day report type in plan mode",
            );
          } else {
            // For non-working types, clear the planned status
            if (day_report_edit.value.day_report_status === "planned") {
              delete day_report_edit.value.day_report_status;
            }
          }
        }
      },
    );

    // Watch for changes in dayReportTypesStore.dayReportTypes to initialize form data again
    // This helps when the types are loaded after the component is mounted
    watch(
      () => dayReportTypesStore.dayReportTypes,
      () => {
        if (dayReportTypesStore.dayReportTypes.length > 0) {
          initFormData();
        }
      },
    );

    // Watch for changes in users to initialize form data again
    // This helps when the users are loaded after the component is mounted
    watch(
      () => users.value,
      () => {
        if (users.value.length > 0) {
          initFormData();
        }
      },
    );

    // Watch for changes in day_report_type_id to set default duration if needed
    watch(
      () => day_report_edit.value.day_report_type_id,
      (newTypeId, oldTypeId) => {
        // Only run this logic if the type changes
        if (!newTypeId) return;

        // In edit mode, only update if type actually changed
        if (edit_mode.value && newTypeId === oldTypeId) return;

        let typeId;

        // Handle both object and direct ID cases
        if (typeof newTypeId === "object") {
          typeId = newTypeId.id;
        } else {
          typeId = newTypeId;
        }

        // Find the type in the day report types store
        const selectedType = dayReportTypesStore.dayReportTypes.find(
          (type) => type.id === typeId,
        );

        // For NON-WORKING types (direct_time = true): Set duration from type on frontend
        // For WORKING types: Clear duration so backend sets default
        if (selectedType) {
          if (selectedType.direct_time === true && selectedType.duration) {
          console.log(
              `Non-working type "${selectedType.name}" - setting duration from type: ${selectedType.duration} seconds`,
          );
          day_report_edit.value.duration = selectedType.duration;
          initTimePickerValues();
          } else {
            // Working type - clear duration, backend will set default
            console.log(
              `Working type "${selectedType.name}" - clearing duration, backend will set default`,
            );
            delete day_report_edit.value.duration;
          }
        }
      },
    );

    // Replace the onMounted function that initializes the time picker:
    onMounted(async () => {
      console.log("DayReportForm mounted");

      try {
        // Load day report types if not already loaded
        if (dayReportTypesStore.dayReportTypes.length === 0) {
          console.log("Loading day report types...");
          await dayReportTypesStore.getAll();
        }

        // Load users if not in hide_user mode and not already loaded
        if (!props.hide_user && users.value.length === 0) {
          console.log("Loading users...");
          await usersStore.getAll();
          console.log(`Loaded ${users.value.length} users`);
        }

        // Initialize form data after loading dependencies
        initFormData();
      } catch (error) {
        console.error("Error initializing DayReportForm:", error);
        alertStore.error(
          "Er is een fout opgetreden bij het laden van het formulier",
        );
      }
    });

    function formatHourLabel(value) {
      if (Number.isInteger(value)) {
        return `${value}u`;
      } else {
        const hours = Math.floor(value);
        const minutes = Math.round((value - hours) * 60);
        return `${hours}:${String(minutes).padStart(2, "0")}`;
      }
    }

    return {
      loading,
      valid,
      rules,
      users,
      day_report_types,
      day_report_edit,
      datepicker,
      timepicker,
      timePickerValue,
      hourSlider,
      planModeHours,
      planModeMinutes,
      formattedDuration,
      isFlexibleDurationType,
      flexibleDurationInput,
      handleFlexibleDurationInput,
      v$,
      show_timepicker,
      edit_mode,
      computedDateFormatted,
      datePickerValue,
      dateErrors,
      durationErrors,
      dayReportTypeErrors,
      dayReportUserErrors,
      submit,
      allowedMinutes,
      onTimeChange,
      confirmTime,
      updateTimeFromSliders,
      updatePlanModeDuration,
      formatHourLabel,
      initTimePickerValues,
      secondsToHHMM,
      hhmmToSeconds,
    };
  },
};
</script>

<style scoped>
/* Add any custom styles here */
</style>
